import type { SignalLookupRow } from "@/lib/api/assistant";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  commodityLabel,
  formatNum,
  formatBarTs,
  formatWindow,
  instrumentLabel,
} from "../utils";

function CountOnly({ row }: { row: SignalLookupRow }) {
  return (
    <div className="p-6 flex items-center gap-4">
      <div className="text-4xl font-extrabold text-indigo-600 tabular-nums">
        {row.signal_count}
      </div>
      <div className="text-sm text-slate-600">
        signal{row.signal_count === 1 ? "" : "s"} matched in this window.
      </div>
    </div>
  );
}

function EventsTable({ row }: { row: SignalLookupRow }) {
  if (row.events.length === 0) {
    return (
      <div className="p-4 text-sm text-slate-500 italic">No matching signals.</div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="text-sm w-full border-collapse">
        <thead>
          <tr className="bg-slate-50 text-slate-600 text-left">
            <th className="px-3 py-2 font-medium">When</th>
            <th className="px-3 py-2 font-medium">Signal Score</th>
            <th className="px-3 py-2 font-medium">Close on Bar</th>
          </tr>
        </thead>
        <tbody>
          {row.events.map((ev, i) => (
            <tr key={i} className="border-t border-slate-200">
              <td className="px-3 py-2 whitespace-nowrap">{formatBarTs(ev.signal_ts, row.bar_display)}</td>
              <td className="px-3 py-2 tabular-nums">{formatNum(ev.signal_value, 1)}</td>
              <td className="px-3 py-2 tabular-nums">{formatNum(ev.close)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({ row }: { row: SignalLookupRow }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            {commodityLabel(row.commodity_id)}
            <Badge
              variant="outline"
              className={`text-[10px] font-semibold ${
                row.signal_type === "buy"
                  ? "border-emerald-400 text-emerald-700 bg-emerald-50"
                  : "border-red-400 text-red-700 bg-red-50"
              }`}
            >
              {row.signal_type === "buy" ? "Buy" : "Sell"}
            </Badge>
            <Badge variant="outline" className="text-[10px]">select: {row.select}</Badge>
            <Badge variant="outline" className="text-[10px]">
              {row.signal_count} match{row.signal_count === 1 ? "" : "es"}
            </Badge>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{instrumentLabel(row.instrument)}</div>
        </div>
        <div className="text-xs text-slate-500 text-right">
          <div>Search: {formatWindow(row.search_window)}</div>
        </div>
      </div>
      {row.select === "count" ? <CountOnly row={row} /> : <EventsTable row={row} />}
      {row.select !== "count" && row.events.length < row.signal_count && (
        <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-t border-amber-200">
          Showing {row.events.length} of {row.signal_count} matches (limited by request).
        </div>
      )}
    </div>
  );
}

export function SignalLookupResult({ rows }: { rows: SignalLookupRow[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Signal Lookup</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 && (
          <div className="text-sm text-slate-500 italic">No rows returned.</div>
        )}
        {rows.map((row, i) => (
          <Row key={`${row.commodity_id}-${i}`} row={row} />
        ))}
      </CardContent>
    </Card>
  );
}
